import { copyToClipboard } from "@/lib/clipboard";
import { AGENT_LAYER_SLOGAN, ROUTES } from "@/lib/routes";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, FileText, Search, Wrench } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

type SkillIndexEntry = {
	id: string;
	title: string;
	description: string;
	category?: string;
	path: string;
};

const FALLBACK_SKILLS: SkillIndexEntry[] = [
	{
		id: "getting-started-agent-layer",
		title: "Getting started",
		description: "Activate your personal agent and create an API key",
		category: "agents",
		path: "/skills/getting-started-agent-layer/SKILL.md",
	},
	{
		id: "agent-gateway",
		title: "Agent Gateway",
		description: "setAgentActive, cycle fees, credits, waivers, vault proxy",
		category: "protocol",
		path: "/skills/agent-gateway/SKILL.md",
	},
	{
		id: "liquidation-agent",
		title: "Liquidation agent",
		description: "Keeper liquidations via agent_gateway",
		category: "protocol",
		path: "/skills/liquidation-agent/SKILL.md",
	},
	{
		id: "cdp-agent",
		title: "CDP agent",
		description: "Vault deposit, mint, repay, withdraw via gateway",
		category: "protocol",
		path: "/skills/cdp-agent/SKILL.md",
	},
	{
		id: "treasury-read",
		title: "Treasury read",
		description: "Surplus buckets, flows, getLiquidityTreasury",
		category: "protocol",
		path: "/skills/treasury-read/SKILL.md",
	},
	{
		id: "treasury-liquidity",
		title: "Treasury liquidity",
		description: "On-protocol sleeves; same API keys as any bot",
		category: "protocol",
		path: "/skills/treasury-liquidity/SKILL.md",
	},
	{
		id: "openclaw-motokodollar",
		title: "OpenClaw",
		description: "External bots with your account API key",
		category: "agents",
		path: "/skills/openclaw-motokodollar/SKILL.md",
	},
	{
		id: "api-key-local-dev",
		title: "API key local dev",
		description: "Hash-only keys (not vetKeys); bot env storage",
		category: "agents",
		path: "/skills/api-key-local-dev/SKILL.md",
	},
];

export default function SkillsPage() {
	const [q, setQ] = useState("");
	const origin =
		typeof window !== "undefined" ? window.location.origin : "";

	const { data, isLoading, isError } = useQuery({
		queryKey: ["v2", "skills-index"],
		queryFn: async (): Promise<SkillIndexEntry[]> => {
			const res = await fetch("/skills/index.json");
			if (!res.ok) throw new Error("Failed to load skills index");
			const json = (await res.json()) as { skills: SkillIndexEntry[] };
			return json.skills ?? [];
		},
		staleTime: 60_000,
		retry: 1,
	});

	const skills = data?.length ? data : FALLBACK_SKILLS;
	const filtered = useMemo(() => {
		const s = q.trim().toLowerCase();
		if (!s) return skills;
		return skills.filter(
			(x) =>
				x.title.toLowerCase().includes(s) ||
				x.description.toLowerCase().includes(s) ||
				x.id.includes(s),
		);
	}, [skills, q]);

	return (
		<div className="container mx-auto space-y-8 px-4 py-8 pb-16">
			<div>
				<p className="font-mono text-[10px] font-bold uppercase tracking-wider text-yellow-400/80">
					{AGENT_LAYER_SLOGAN}
				</p>
				<h1 className="mt-1 font-mono text-xl font-bold text-yellow-400 sm:text-2xl">
					Agent skills
				</h1>
				<p className="mt-2 max-w-2xl font-mono text-sm leading-relaxed text-yellow-400/75">
					A library for agents, and the humans who direct them. Load a skill
					before inventing candid flows — correct fees, env, and pitfalls.
				</p>
			</div>

			<section className="rounded-xl border border-yellow-500/40 bg-black/80 p-5">
				<div className="flex flex-wrap items-start gap-3">
					<span className="flex size-10 shrink-0 items-center justify-center rounded-md border border-yellow-500/40 bg-yellow-400/10">
						<FileText className="size-5 text-yellow-400" aria-hidden />
					</span>
					<div className="min-w-0 flex-1">
						<p className="font-mono text-[10px] font-bold uppercase text-yellow-400/70">
							Machine map
						</p>
						<h2 className="font-mono text-base font-bold text-yellow-400">
							llms.txt · skills index
						</h2>
						<p className="mt-1 font-mono text-xs text-yellow-400/60">
							Open the protocol map in a new tab. Point agents at the index.
						</p>
					</div>
				</div>
				<div className="mt-4 flex flex-wrap gap-2">
					<a
						href="/llms.txt"
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex min-h-10 items-center gap-1.5 rounded-md border border-yellow-400 bg-yellow-400 px-3 font-mono text-xs font-bold text-black hover:bg-yellow-300"
					>
						View llms.txt
						<ExternalLink className="size-3.5" aria-hidden />
					</a>
					<a
						href="/llms-full.txt"
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex min-h-10 items-center gap-1.5 rounded-md border border-yellow-500/50 px-3 font-mono text-xs font-semibold text-yellow-400 hover:bg-yellow-400/10"
					>
						llms-full.txt
						<ExternalLink className="size-3.5" aria-hidden />
					</a>
					<button
						type="button"
						className="inline-flex min-h-10 items-center rounded-md border border-yellow-500/50 px-3 font-mono text-xs font-semibold text-yellow-400 hover:bg-yellow-400/10"
						onClick={async () => {
							const ok = await copyToClipboard(`${origin}/llms.txt`);
							if (ok) toast.success("llms.txt URL copied");
							else toast.error("Copy failed — select and copy manually");
						}}
					>
						Copy llms URL
					</button>
					<a
						href="/skills/index.json"
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex min-h-10 items-center gap-1.5 rounded-md border border-yellow-500/40 px-3 font-mono text-xs font-semibold text-yellow-400 hover:bg-yellow-400/10"
					>
						index.json
						<ExternalLink className="size-3.5" aria-hidden />
					</a>
					<Link
						to={ROUTES.agents}
						className="inline-flex min-h-10 items-center rounded-md border border-yellow-500/30 px-3 font-mono text-xs font-semibold text-yellow-400/80 hover:bg-yellow-400/10"
					>
						Activate agent
					</Link>
				</div>
			</section>

			<section className="space-y-4">
				<div className="flex flex-wrap items-end justify-between gap-3">
					<div>
						<p className="font-mono text-[10px] font-bold uppercase text-yellow-400/70">
							Catalog
						</p>
						<h2 className="font-mono text-base font-bold text-yellow-400">
							Protocol skills
						</h2>
						{isError ? (
							<p className="mt-1 font-mono text-[11px] text-yellow-500/70">
								Index offline — showing fallback list
							</p>
						) : null}
					</div>
					<label className="relative block w-full max-w-xs">
						<Search
							className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-yellow-400/50"
							aria-hidden
						/>
						<input
							value={q}
							onChange={(e) => setQ(e.target.value)}
							placeholder="Search skills…"
							className="h-10 w-full rounded-md border border-yellow-500/30 bg-black/60 py-2 pl-8 pr-3 font-mono text-xs text-yellow-100 placeholder:text-yellow-400/40 outline-none focus:border-yellow-400/60"
						/>
					</label>
				</div>

				{isLoading ? (
					<div className="grid gap-3 sm:grid-cols-2">
						{Array.from({ length: 4 }).map((_, i) => (
							<div
								key={i}
								className="h-28 animate-pulse rounded-xl border border-yellow-500/20 bg-black/60"
							/>
						))}
					</div>
				) : (
					<ul className="grid gap-3 sm:grid-cols-2">
						{filtered.map((s) => (
							<li key={s.id}>
								<a
									href={s.path}
									target="_blank"
									rel="noopener noreferrer"
									className={cn(
										"flex h-full items-start gap-3 rounded-xl border border-yellow-500/30 bg-black/80 p-5 transition",
										"hover:border-yellow-400/60 hover:bg-yellow-400/5",
									)}
								>
									<span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-yellow-500/40 bg-yellow-400/10">
										<Wrench className="size-4 text-yellow-400" aria-hidden />
									</span>
									<div className="min-w-0 flex-1">
										<div className="flex items-start justify-between gap-2">
											<h3 className="font-mono text-sm font-bold text-yellow-400">
												{s.title}
											</h3>
											<ExternalLink
												className="size-3.5 shrink-0 text-yellow-400/50"
												aria-hidden
											/>
										</div>
										<p className="mt-1 font-mono text-xs leading-relaxed text-yellow-400/65">
											{s.description}
										</p>
										<p className="mt-2 font-mono text-[10px] text-yellow-400/40">
											{s.id}
										</p>
									</div>
								</a>
							</li>
						))}
					</ul>
				)}
			</section>

			<p className="font-mono text-xs text-yellow-400/50">
				<Link to={ROUTES.docs} className="underline hover:text-yellow-300">
					Docs
				</Link>
				{" · "}
				<Link to={ROUTES.agents} className="underline hover:text-yellow-300">
					Agents
				</Link>
				{" · "}
				<Link to={ROUTES.liquidity} className="underline hover:text-yellow-300">
					Liquidity inventory
				</Link>
			</p>
		</div>
	);
}
